<script setup lang="ts">
import { useStore } from "vuex";
import { computed, onBeforeUnmount, customRef, watch } from "vue";
import Length from "../../../source/js/shared/models/Geometry/Length";
import ValidationMessage from "./UI/ValidationMessage.vue";
import {formatForInputField, stripNonNumeric} from "@/functions/numbers";
import {addSizeToDataLayer} from "@/functions/analytics";
import DimensionInput from "@/components/UI/DimensionInput.vue";
import ShapeInputFieldsWrapper from "@/components/UI/ShapeInputFieldsWrapper.vue";
import MessageContainer from "@/components/UI/MessageContainer.vue";

const store = useStore();

const isCutoutValid = computed(() => store.getters.isCutoutValid);
const getCutoutValidationMessage = computed(() => store.getters.getCutoutValidationMessage);
const shapeSupportCutoutRotation = computed(() => store.getters.shapeSupportCutoutRotation);
const cutoutOutsideModel = computed(() => store.getters.cutoutOutsideModel);
const lastGeneratedConfiguration = computed(() => store.getters.lastGeneratedConfiguration);
const cutoutOffsetLeft = computed( () => store.getters.cutoutOffsetLeft );
const cutoutOffsetBottom = computed( () => store.getters.cutoutOffsetBottom );
const cutoutShape = computed( () => store.getters.cutoutShape );
const cutoutRotation = computed( () => store.getters.cutoutRotation );

const rotation = customRef(() => {
	return {
		get: () => cutoutRotation.value,
		set: (value) => {
			if ( value.length === 0 ) return;

			store.commit('updateCutoutRotation', stripNonNumeric( value ));
			store.commit('updateUsingDefaultCutoutDimensions', false);
		},
	};
});

const offsetLeft = customRef(() => {
	return {
		get: () => formatForInputField( new Length( cutoutOffsetLeft.value ).in_cm() ),
		set: (value: string|number) => {
			store.commit('updateCutoutOffsetLeft', Length.from_cm( stripNonNumeric(value) ).in_mm() );
			store.commit('updateUsingDefaultCutoutDimensions', false );
		},
	};
});

const offsetBottom = customRef(() => {
	return {
		get: () => formatForInputField( new Length( cutoutOffsetBottom.value ).in_cm() ),
		set: (value: string|number) => {
			store.commit('updateCutoutOffsetBottom', Length.from_cm( stripNonNumeric(value) ).in_mm() );
			store.commit('updateUsingDefaultCutoutDimensions', false );
		},
	};
});

watch(cutoutShape, async( newShape, previousShape ) => {
	if ( newShape !== previousShape ) {
		store.commit('resetCutoutDimension', previousShape );
	}
});

if (lastGeneratedConfiguration.value.cutout) {
	store.commit('updateUsingDefaultCutoutDimensions', false);
}

document.addEventListener('focusin', (event) =>
	store.commit('updateFocusedElement', event)
);

onBeforeUnmount(() => {
	document.removeEventListener('focusin', () =>
		store.commit('updateFocusedElement')
	);
});
</script>

<template>
	<div>
		<shape-input-fields-wrapper>
			<template #legend>
				{{ $i18n.cutout.position.title }}
			</template>

			<dimension-input
				inputRef="cutoutOffsetLeft"
				id="cutoutOffsetLeft"
				name="cutoutOffsetLeft"
				unit="cm"
				:label="$i18n.cutout.position.offset_left"
				:invalid="!isCutoutValid('cutoutOffsetLeft') || !isCutoutValid('cutoutOffset')"
				:onBlur="() => addSizeToDataLayer('cutoutOffsetLeft', +offsetLeft, `${cutoutShape} (cutout)`)"
				v-model="offsetLeft"
			/>

			<dimension-input
				inputRef="cutoutOffsetBottom"
				id="cutoutOffsetBottom"
				name="cutoutOffsetBottom"
				unit="cm"
				:label="$i18n.cutout.position.offset_bottom"
				:invalid="!isCutoutValid('cutoutOffsetBottom') || !isCutoutValid('cutoutOffset')"
				:onBlur="() => addSizeToDataLayer('cutoutOffsetBottom', +offsetBottom, `${cutoutShape} (cutout)`)"
				v-model="offsetBottom"
			/>

			<dimension-input
				v-if="shapeSupportCutoutRotation"
				inputRef="cutoutRotation"
				id="cutoutRotation"
				name="cutoutRotation"
				type="number"
				unit="&deg;"
				:label="$i18n.cutout.position.rotation"
				:onBlur="() => addSizeToDataLayer('cutoutRotation', +rotation, `${cutoutShape} (cutout)`)"
				v-model="rotation"
				:step="1"
				:invalid="false"
			/>
		</shape-input-fields-wrapper>

		<message-container>
			<!-- Message for cutoutOffset -->
			<validation-message
				v-if="!isCutoutValid('cutoutOffset')"
				input-id="cutoutOffset"
			>
				{{ getCutoutValidationMessage('cutoutOffset') }}
			</validation-message>

			<!-- Message for cutoutOffsetLeft -->
			<validation-message
				v-if="!isCutoutValid('cutoutOffsetLeft')"
				input-id="cutoutOffsetLeft"
			>
				{{ getCutoutValidationMessage('cutoutOffsetLeft') }}
			</validation-message>

			<!-- Message for cutoutOffsetBottom -->
			<validation-message
				v-if="!isCutoutValid('cutoutOffsetBottom')"
				input-id="cutoutOffsetBottom"
			>
				{{ getCutoutValidationMessage('cutoutOffsetBottom') }}
			</validation-message>

			<validation-message
				type="notice"
			>
				{{ $i18n.cutoutNotice }}
			</validation-message>

			<validation-message
				v-if="cutoutOutsideModel"
				type="notice"
			>
				{{ $i18n.cutoutOutsideShapeNotice }}
			</validation-message>
		</message-container>
	</div>
</template>
